import { type FC } from 'react';
import { Bell, Menu } from 'lucide-react';

/**
 * TopAppBar — full-width sticky header for the app frame (flat Material-3).
 * Left: mobile sidebar trigger (below lg) + the current page title.
 * Right: notification bell (opens the notifications center) + avatar (opens
 * the account/menu sheet).
 */

export interface TopbarProps {
  title: string;
  initials: string;
  onOpenMobileSidebar: () => void;
  onOpenHamburger: () => void;
  onOpenNotifications?: () => void;
  hasUnreadNotifications?: boolean;
}

export const Topbar: FC<TopbarProps> = ({
  title,
  initials,
  onOpenMobileSidebar,
  onOpenHamburger,
  onOpenNotifications,
  hasUnreadNotifications = false,
}) => {
  return (
    <header className="sticky top-0 z-30 h-16 w-full bg-background/80 backdrop-blur-md border-b border-outline-variant flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onOpenMobileSidebar}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container-high/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-label="Open navigation"
        >
          <Menu className="w-5 h-5" />
        </button>
        <h2 className="font-display text-lg font-semibold text-on-surface truncate">
          {title}
        </h2>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onOpenNotifications}
          className="relative w-10 h-10 flex items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container-high/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {hasUnreadNotifications && (
            <span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-error ring-2 ring-background" />
          )}
        </button>
        <button
          onClick={onOpenHamburger}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-primary text-on-primary text-label font-semibold hover:bg-primary-container transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-label="Account menu"
        >
          {initials}
        </button>
      </div>
    </header>
  );
};

export default Topbar;
